import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useAuthStore } from '../store/useAuthStore.js';
import { api } from '../../../shared/api/api.js';
import {
  ProfileSection,
  getRoleBadgeInfo,
} from './profile/profileConstants.js';
import { ProfileUnauthenticated } from './profile/ProfileUnauthenticated.js';
import { ProfileHeaderBanner } from './profile/ProfileHeaderBanner.js';
import { ProfileDetailsTab } from './profile/ProfileDetailsTab.js';
import { ProfileNameTab } from './profile/ProfileNameTab.js';
import { ProfilePasswordTab } from './profile/ProfilePasswordTab.js';

interface UserProfileProps {
  isOpen?: boolean;
  onClose?: () => void;
  isModal?: boolean;
}

export const UserProfile: React.FC<UserProfileProps> = ({
  isOpen = true,
  onClose,
  isModal = false,
}) => {
  const { user, updateUser } = useAuthStore();

  // Tab navigation inside profile
  const [activeSection, setActiveSection] = useState<ProfileSection>('details');

  // Display Name form state
  const [displayName, setDisplayName] = useState(user?.name || '');
  const [organization, setOrganization] = useState(user?.organization || '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSuccess, setNameSuccess] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // Copy ID feedback
  const [copiedId, setCopiedId] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [isConvertingAvatar, setIsConvertingAvatar] = useState(false);
  const [avatarCompressionStats, setAvatarCompressionStats] = useState<{
    originalSizeKB: number;
    compressedSizeKB: number;
    savingsPercentage: number;
  } | null>(null);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingAvatar(true);
    setIsConvertingAvatar(true);
    setAvatarCompressionStats(null);
    setNameError(null);
    setNameSuccess(null);

    const originalSizeKB = Math.max(1, Math.round(file.size / 1024));

    try {
      const { convertImageToWebp } = await import('../../../shared/utils/imageUtils.js');
      const webpFile = await convertImageToWebp(file, 0.85);

      const compressedSizeKB = Math.max(1, Math.round(webpFile.size / 1024));
      const savingsPercentage = originalSizeKB > 0
        ? Math.max(0, Math.round(((originalSizeKB - compressedSizeKB) / originalSizeKB) * 100))
        : 0;

      setAvatarCompressionStats({
        originalSizeKB,
        compressedSizeKB,
        savingsPercentage,
      });

      setIsConvertingAvatar(false);

      const { profileImageUrl } = await api.uploadProfileImage(webpFile);

      const res = await api.updateProfile({
        profileImageUrl,
      });

      updateUser(res.user, res.token);
      setNameSuccess('Foto profil berhasil dikonversi ke format WebP dan disimpan ke Cloudflare R2.');
      setTimeout(() => setNameSuccess(null), 6000);
    } catch (err: any) {
      setNameError(err.message || 'Gagal mengunggah foto profil.');
    } finally {
      setIsConvertingAvatar(false);
      setUploadingAvatar(false);
      if (e.target) e.target.value = '';
    }
  };

  // Synchronize state when user changes
  useEffect(() => {
    if (user) {
      setDisplayName(user.name || '');
      setOrganization(user.organization || '');
    }
  }, [user]);

  if (isModal && !isOpen) {
    // Return null when modal closed, but AnimatePresence inside Portal handles exiting
  }

  if (!user) {
    return <ProfileUnauthenticated onClose={onClose} isModal={isModal} />;
  }

  const handleCopyId = () => {
    if (!user?.id) return;
    navigator.clipboard.writeText(user.id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  // Submit Display Name & Organization change
  const handleUpdateName = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(null);
    setNameSuccess(null);

    const trimmedName = displayName.trim();
    if (!trimmedName || trimmedName.length < 2) {
      setNameError('Nama tampilan minimal 2 karakter.');
      return;
    }

    setNameSaving(true);
    try {
      const res = await api.updateProfile({
        name: trimmedName,
        organization: organization.trim() || undefined,
      });

      // Update auth store with fresh user & token
      updateUser(res.user, res.token);
      setNameSuccess('Nama tampilan profil berhasil diperbarui.');
      setTimeout(() => setNameSuccess(null), 4000);
    } catch (err: any) {
      setNameError(err.message || 'Gagal memperbarui nama akun.');
    } finally {
      setNameSaving(false);
    }
  };

  const handleResetName = () => {
    setDisplayName(user.name);
    setOrganization(user.organization || '');
    setNameError(null);
    setNameSuccess(null);
  };

  // Submit Password update
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!currentPassword) {
      setPasswordError('Harap masukkan kata sandi Anda saat ini.');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Kata sandi baru minimal 6 karakter.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Konfirmasi kata sandi baru tidak cocok.');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('Kata sandi baru tidak boleh sama dengan kata sandi saat ini.');
      return;
    }

    setPasswordSaving(true);
    try {
      const res = await api.updateProfile({
        currentPassword,
        newPassword,
      });

      if (res.token) {
        updateUser(res.user, res.token);
      }
      setPasswordSuccess('Kata sandi berhasil diperbarui dengan aman.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(null), 4000);
    } catch (err: any) {
      setPasswordError(err.message || 'Gagal mengubah kata sandi.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleResetPassword = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(null);
  };

  const roleBadgeInfo = getRoleBadgeInfo(user.role);

  const content = (
    <div className="bg-white md:rounded-3xl border-0 md:border border-slate-200/80 shadow-none md:shadow-xl overflow-hidden max-w-4xl w-full mx-auto min-h-screen md:min-h-0 flex flex-col">
      {/* Header Banner & Navigation Tabs */}
      <ProfileHeaderBanner
        user={user}
        roleBadgeInfo={roleBadgeInfo}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        isModal={isModal}
        onClose={onClose}
      />

      {/* Main Body Content based on activeSection */}
      <div className="p-5 sm:p-8 overflow-hidden flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 12, filter: 'blur(3px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -12, filter: 'blur(3px)' }}
            transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
          >
            {activeSection === 'details' && (
              <ProfileDetailsTab
                user={user}
                roleBadgeInfo={roleBadgeInfo}
                copiedId={copiedId}
                onCopyId={handleCopyId}
                onNavigateToName={() => setActiveSection('name')}
                onNavigateToPassword={() => setActiveSection('password')}
              />
            )}

            {activeSection === 'name' && (
              <ProfileNameTab
                user={user}
                displayName={displayName}
                setDisplayName={setDisplayName}
                organization={organization}
                setOrganization={setOrganization}
                nameSaving={nameSaving}
                nameSuccess={nameSuccess}
                nameError={nameError}
                onSubmit={handleUpdateName}
                onReset={handleResetName}
                onAvatarChange={handleAvatarChange}
                uploadingAvatar={uploadingAvatar}
                isConvertingAvatar={isConvertingAvatar}
                avatarCompressionStats={avatarCompressionStats}
                onDismissAvatarStats={() => setAvatarCompressionStats(null)}
              />
            )}

            {activeSection === 'password' && (
              <ProfilePasswordTab
                currentPassword={currentPassword}
                setCurrentPassword={setCurrentPassword}
                newPassword={newPassword}
                setNewPassword={setNewPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
                showCurrentPassword={showCurrentPassword}
                setShowCurrentPassword={setShowCurrentPassword}
                showNewPassword={showNewPassword}
                setShowNewPassword={setShowNewPassword}
                showConfirmPassword={showConfirmPassword}
                setShowConfirmPassword={setShowConfirmPassword}
                passwordSaving={passwordSaving}
                passwordSuccess={passwordSuccess}
                passwordError={passwordError}
                onSubmit={handleUpdatePassword}
                onReset={handleResetPassword}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );

  if (isModal) {
    return createPortal(
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 15 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-50 bg-white md:bg-slate-900/60 md:backdrop-blur-xs overflow-y-auto flex md:p-6 lg:p-8 justify-center items-start"
          >
            <div 
              className="w-full max-w-4xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {content}
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    );
  }

  return content;
};
